import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import LanguageToggle from "./LanguageToggle";

const LOGO_URL = "/logo.png";

const Header = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/",         label: t("nav.home") },
    { to: "/services", label: t("nav.services") },
    { to: "/devis",    label: t("nav.quote") },
    { to: "/blog",     label: t("nav.blog") },
    { to: "/a-propos", label: t("nav.about") },
    { to: "/contact",  label: t("nav.contact") },
  ];

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled || open
          ? "bg-white/90 backdrop-blur-md border-b border-slate-200/60 shadow-sm"
          : "border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="container-x flex items-center justify-between h-16">

        {/* ── Logo ─────────────────────────────────────────── */}
        <Link to="/" className="flex items-center gap-3 shrink-0 group" data-testid="header-logo-link">
          <img
            src={LOGO_URL}
            alt="BIONET SOLUTIONS"
            className="h-14 w-auto object-contain"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-heading font-extrabold text-base text-slate-900 tracking-tight leading-none">
              BIONET SOLUTIONS
            </span>
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-medium leading-none mt-0.5">
              Nettoyage &amp; Services
            </span>
          </div>
        </Link>

        {/* ── Nav desktop ──────────────────────────────────── */}
        <nav className="hidden lg:flex items-center gap-0.5" data-testid="header-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-150 ${
                  isActive
                    ? "text-brand-green underline underline-offset-4 decoration-brand-green/50"
                    : "text-slate-600 hover:text-brand-green hover:bg-emerald-50/60"
                }`
              }
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* ── Actions desktop ───────────────────────────────── */}
        <div className="flex items-center gap-2.5">

          {/* Téléphone */}
          <a
            href="tel:+33649502801"
            className="hidden lg:inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-slate-600 hover:text-brand-green transition-colors duration-200"
            data-testid="header-phone-cta"
          >
            <Phone className="h-3.5 w-3.5 shrink-0" />
            06 49 50 28 01
          </a>

          <LanguageToggle />

          {/* CTA Devis */}
          <Link
            to="/devis"
            className="hidden md:inline-flex items-center rounded-full bg-brand-green text-white px-4 py-2 text-sm font-semibold whitespace-nowrap hover:bg-emerald-700 transition-colors duration-200"
            data-testid="header-quote-cta"
          >
            {t("common.cta_quote_short")}
          </Link>

          {/* Téléphone icône mobile */}
          <a
            href="tel:+33649502801"
            className="lg:hidden p-2.5 rounded-lg text-brand-green hover:bg-emerald-50 transition-colors"
            aria-label="Appeler BIONET SOLUTIONS"
            data-testid="mobile-phone-icon"
          >
            <Phone className="h-5 w-5" />
          </a>

          {/* Hamburger animé */}
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2.5 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="menu"
            data-testid="mobile-menu-toggle"
          >
            <AnimatePresence mode="wait" initial={false}>
              {open ? (
                <motion.span key="close" className="block"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <X className="h-5 w-5" />
                </motion.span>
              ) : (
                <motion.span key="menu" className="block"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Menu className="h-5 w-5" />
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </div>

      {/* ── Menu mobile slide-down ────────────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: "hidden" }}
            className="lg:hidden border-t border-slate-200/70 bg-white"
            data-testid="mobile-menu"
          >
            <div className="container-x py-4 flex flex-col gap-0.5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-emerald-50 text-brand-green"
                        : "text-slate-700 hover:bg-slate-50 hover:text-brand-green"
                    }`
                  }
                  data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                >
                  {l.label}
                </NavLink>
              ))}

              <div className="flex gap-2 pt-3 mt-1 border-t border-slate-100">
                <a
                  href="tel:+33649502801"
                  className="flex-1 flex items-center justify-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm text-slate-700 hover:border-brand-green hover:text-brand-green transition-colors"
                  data-testid="mobile-phone-cta"
                >
                  <Phone className="h-4 w-4" /> 06 49 50 28 01
                </a>
                <Link
                  to="/devis"
                  onClick={() => setOpen(false)}
                  className="flex-1 flex items-center justify-center rounded-full bg-brand-green text-white px-4 py-2.5 text-sm font-semibold hover:bg-emerald-700 transition-colors"
                  data-testid="mobile-quote-cta"
                >
                  {t("common.cta_quote_short")}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
